import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { AuthActions } from "src/redux/Auth/actions";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import { SettingsActions } from "src/redux/Settings/actions";
import Dropdown from "../../common/Dropdown";

import RoleSwitch from "./RoleSwitch";
import ViewSwitch from "./ViewSwitch";
import { CURRENCY_OPTIONS } from "src/redux/MarketData/constants";

import styles from "./styles.module.scss";
import stylesRoleSwitch from "./RoleSwitch/styles.module.scss";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { ROLE } from "src/redux/TopBar/constants";
import { ModalActions } from "src/redux/Modal/actions";


/**
 * The four legal documents, opened as pages rather than downloaded. Served
 * from the same origin as the app, so a counterparty user reaches them on
 * their own host. Same set the consent gate lists.
 */
const LEGAL_DOCUMENTS = [
    { label: "Terms of Use (Korean)", href: "/legal/terms_of_use_kor.html" },
    { label: "Terms of Use (English)", href: "/legal/terms_of_use_eng.html" },
    { label: "Privacy Policy (Korean)", href: "/legal/privacy_policy_kor.html" },
    { label: "Privacy Policy (English)", href: "/legal/privacy_policy_eng.html" },
];

const TopBar = () => {
    const [isProfileClicked, setIsProfileClicked] = useState(false);
    // Terms & Privacy expands in place rather than navigating, so the menu
    // stays where it is while the documents open in their own tabs.
    const [termsOpen, setTermsOpen] = useState(false);
    const dispatch = useDispatch();

    const logout = () => dispatch(AuthActions.Logout());
    const getAvailabilitySettings = () =>
        dispatch(SettingsActions.GetAvailabilitySettings());
    const getLenderBlockedSecuritySettings = () =>
        dispatch(SettingsActions.GetLenderBlockedSecuritySettings());
    const getListSettings = () => dispatch(SettingsActions.GetListSettings());
    const getPeerCptySettings = () =>
        dispatch(SettingsActions.GetPeerCptySettingsUi());
    const getAOSettings = () => dispatch(SettingsActions.GetAOSettings());
    const getAccountSettings = () =>
        dispatch(SettingsActions.GetAccountSettings());
    const getEmailSettings = () => dispatch(SettingsActions.GetEmailSettings());
    const getFundSettings = () => dispatch(SettingsActions.GetFundSettings());
    const updateUserSettings = (userSettings) =>
        dispatch(SettingsActions.UpdateUserSettings(userSettings));
    const getUserSettings = () => dispatch(SettingsActions.OpenUserSettings());
    const handleProfileClick = () => {
        // Closing the profile menu collapses Terms & Privacy with it, so
        // reopening starts from the same place every time rather than resuming
        // whatever the last visit left expanded.
        if (isProfileClicked) setTermsOpen(false);
        setIsProfileClicked(!isProfileClicked);
    };

    const role = useSelector(TopBarSelectors.Role);
    const user = useSelector(AuthSelectors.User);
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);
    const userRole = useSelector(AuthSelectors.UserRole);
    const myCounterParty = useSelector(
        CounterPartysSelectors.GetMyCounterParty
    );
    const userSettings = useSelector(SettingsSelectors.UserSettings);

    const {
        "enable-list-level-availability": enabledListLevelAvailability,
        "enable-lender-block-securities": enabledLenderBlockSecurities,
        // Limited UI: spec §3 disables Auto-offer (Lender) and the
        // standalone Availability Settings panel. Hide the profile-menu
        // entries; backend (SettingsController.updateCptySettings)
        // also silently blanks auto-offer fields as defence-in-depth.
        "limited-ui": limitedUi,
    } = useSelector(SettingsSelectors.FeatureFlags);

    useEffect(() => {
        /* this empties opened modals persisted in the Modals state, just to prevent a situation where
        login modal is still opened when user logs in.
        */
        dispatch(ModalActions.CloseAll());
    }, []);

    const handleProfileWrapperClick = (e) => {
        // Terms & Privacy expands its documents in place, and each document
        // opens in its own tab, so neither the toggle nor a document row
        // should close the profile menu behind it. Every other menu item does.
        for (let element = e.target; element; element = element.parentNode) {
            if (
                element.classList?.contains(styles.termsToggle) ||
                element.classList?.contains(styles.subMenuItem)
            ) {
                return;
            }
        }
        for (let element = e.target; element; element = element.parentNode) {
            // closes profile controller on clicking outside its area
            if (element.parentElement === null) {
                handleProfileClick();
                break;
            }
            // closes profile controller on clicking a menu inside profile
            if (
                element.parentElement.classList.contains(
                    stylesRoleSwitch.role
                ) ||
                element.classList.contains(styles.menuItem)
            ) {
                handleProfileClick();
                break;
            }
            // ignores clicks within profile controller
            if (
                element.parentElement.classList.contains(styles.profileWrapper)
            ) {
                break;
            }
        }
    };

    const isAdminRole = userRole === "supervisor" || userRole === "operator";

    const renderProfile = () =>
        isProfileClicked && (
            <div
                className={styles.profileWrapper}
                onClick={handleProfileWrapperClick}
            >
                <div className={styles.profileDropdown}>
                    {!isAdminRole && (
                        <>
                            <div
                                className={[
                                    styles.profileInstitution,
                                    styles[role],
                                ].join(" ")}
                            >
                                <div className={styles.profileTitle}>Institution</div>
                                <div className={styles.profileValue}>
                                    {myCounterParty.name}
                                </div>
                            </div>
                            <RoleSwitch className={styles.switchRole} />
                            <div
                                className={styles.menuItem}
                                data-testid="user-settings"
                                onClick={getUserSettings}
                            >
                                Personal Settings
                            </div>
                            <div
                                className={styles.menuItem}
                                data-testid="account-settings"
                                onClick={getAccountSettings}
                            >
                                Intermediary Accounts
                            </div>
                            <div
                                className={styles.menuItem}
                                data-testid="known-cpty-settings"
                                onClick={getPeerCptySettings}
                            >
                                Counterparty Settings
                            </div>
                            <div
                                className={styles.menuItem}
                                data-testid="email-settings"
                                onClick={getEmailSettings}
                            >
                                Records Settings
                            </div>
                            {role === ROLE.LENDER && (
                                <>
                                    {!enabledListLevelAvailability && !limitedUi && (
                                        <div
                                            className={styles.menuItem}
                                            data-testid="availability-settings"
                                            onClick={getAvailabilitySettings}
                                        >
                                            Availability Settings
                                        </div>
                                    )}
                                    {!limitedUi && (
                                        <div
                                            className={styles.menuItem}
                                            data-testid="auto-offer-settings"
                                            onClick={getAOSettings}
                                        >
                                            Auto-Offer Settings
                                        </div>
                                    )}
                                    <div
                                        className={styles.menuItem}
                                        data-testid="list-settings"
                                        onClick={getListSettings}
                                    >
                                        List Settings
                                    </div>
                                </>
                            )}

                            {role === ROLE.LENDER && (
                                <>
                                    {enabledLenderBlockSecurities && (
                                        <div
                                            className={styles.menuItem}
                                            data-testid="lender-blocked-securities-settings"
                                            onClick={getLenderBlockedSecuritySettings}
                                        >
                                            Restricted Securities List
                                        </div>
                                    )}
                                </>
                            )}

                            {role === ROLE.BORROWER && (
                                <>
                                    <div
                                        className={styles.menuItem}
                                        data-testid="fund-settings"
                                        onClick={getFundSettings}
                                    >
                                        Fund Settings
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    <div
                        className={`${styles.menuItem} ${styles.termsToggle} ${
                            termsOpen ? styles.expanded : ""
                        }`}
                        data-testid="terms-and-privacy"
                        onClick={() => setTermsOpen(!termsOpen)}
                    >
                        Terms &amp; Privacy
                    </div>
                    {termsOpen &&
                        LEGAL_DOCUMENTS.map((doc) => (
                            <a
                                key={doc.href}
                                className={`${styles.menuItem} ${styles.subMenuItem}`}
                                data-testid={`legal-${doc.href}`}
                                href={doc.href}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {doc.label}
                            </a>
                        ))}
                    <div
                        className={styles.menuItem}
                        data-testid="logout-button"
                        onClick={logout}
                    >
                        Logout
                    </div>
                </div>
            </div>
        );

    // Environment label from the admin hostname (admin.<env>.truweb.io),
    // shown next to the logo on every page so operators know which
    // environment they are administering.
    const envMatch = window.location.hostname.match(/^admin\.([^.]+)\./);
    const environment = envMatch ? envMatch[1] : null;

    return (
        <div className={styles.topbar}>
            <div className={styles.logoArea}>
                <div className={styles.logoSmall} />
                {environment && (
                    <div className={styles.envBadge} data-testid="env-badge">
                        {environment}
                    </div>
                )}
            </div>
            <ViewSwitch />
            <div className={styles.topbarRight}>
                {!isAdminRole && (
                    <>
                        <div className={styles.topbarBorder} />
                        <div className={styles.preferredCcy}>
                            <Dropdown
                                mainStyle="thick-black-2"
                                value={
                                    CURRENCY_OPTIONS.find(
                                        (e) => e.value === userSettings.preferredCcy
                                    ) || CURRENCY_OPTIONS[0]
                                }
                                options={CURRENCY_OPTIONS}
                                onChange={(ccy) =>
                                    updateUserSettings({
                                        ...userSettings,
                                        preferredCcy: ccy.value,
                                    })
                                }
                                dataTestId="user-preferred-ccy"
                                ariaLabel="Select Currency"
                                isDisabled={false}
                            />
                        </div>
                    </>
                )}

                <div className={styles.topbarBorder} />
                <div
                    className={styles.profile}
                    data-testid="open-profile-button"
                    onClick={handleProfileClick}
                >
                    {!isAdminRole && (
                        <div className={[styles.roleArea, styles[role]].join(" ")}>
                            {role ? role.charAt(0).toUpperCase() : "-"}
                        </div>
                    )}
                    <div className={styles.userInfo}>
                        <div className={styles.userName}>
                            {isAdminRole ? (userRole || "").charAt(0).toUpperCase() + (userRole || "").slice(1) : myCounterParty.name}
                        </div>
                        <div className={styles.userEmail}>
                            {user.attributes.email}
                        </div>
                    </div>
                    {!isTraderRole && !isAdminRole && (
                        <div className={styles.readOnlyBadge}>
                            {(userRole || "").toUpperCase()}
                        </div>
                    )}
                    <div className={styles.dropdownArr} />
                </div>
                {renderProfile()}
            </div>
        </div>
    );
};

export default TopBar;
